import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Truck, 
  Shield,
  Upload,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  AlertCircle,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { 
    icon: Clock, 
    color: 'bg-yellow-500/15 text-yellow-600',
    label: 'Pending Submission',
    description: 'Please submit your verification documents below.'
  },
  under_review: { 
    icon: Clock, 
    color: 'bg-blue-500/15 text-blue-500',
    label: 'Under Review',
    description: 'Your documents are being reviewed. This usually takes 24-48 hours.'
  },
  verified: { 
    icon: CheckCircle, 
    color: 'bg-green-500/15 text-green-500',
    label: 'Verified',
    description: 'Your account is verified. You can now access all features.'
  },
  rejected: { 
    icon: XCircle, 
    color: 'bg-red-500/15 text-red-500',
    label: 'Rejected',
    description: 'Your verification was rejected. Please review the notes and resubmit.'
  }
};

export default function VerificationPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [formData, setFormData] = useState({
    abn: '',
    company_name: ''
  });
  const [files, setFiles] = useState({
    photo_id: null,
    insurance: null,
    business_registration: null
  });

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
      if (response.data.abn) {
        setFormData(prev => ({ ...prev, abn: response.data.abn }));
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (field, file) => {
    setFiles(prev => ({ ...prev, [field]: file }));
  };

  const formatABN = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0,2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0,2)} ${digits.slice(2,5)} ${digits.slice(5)}`;
    return `${digits.slice(0,2)} ${digits.slice(2,5)} ${digits.slice(5,8)} ${digits.slice(8)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const abnClean = formData.abn.replace(/\s/g, '');
    if (abnClean.length !== 11) {
      toast.error('ABN must be 11 digits');
      return;
    }
    
    if (!files.photo_id || !files.insurance) {
      toast.error('Please upload Photo ID and Insurance documents');
      return;
    }

    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('abn', abnClean);
      formDataToSend.append('company_name', formData.company_name);
      formDataToSend.append('photo_id', files.photo_id);
      formDataToSend.append('insurance', files.insurance);
      if (files.business_registration) {
        formDataToSend.append('business_registration', files.business_registration);
      }

      await axios.post(`${API_URL}/api/verification/submit`, formDataToSend, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Documents submitted successfully! We\'ll review within 24-48 hours.');
      fetchVerificationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit documents');
    } finally {
      setSubmitting(false);
    }
  };

  const status = verificationStatus?.verification_status || 'pending';
  const StatusIcon = statusConfig[status]?.icon || Clock;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
              THE HOTSHOT MARKETPLACE
            </span>
          </Link>
          <Link to="/provider">
            <Button variant="outline">Back to Dashboard</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              PROVIDER VERIFICATION
            </h1>
            <p className="text-muted-foreground">
              Verify your identity to build trust with customers
            </p>
          </div>

          {/* Status Card */}
          <div className={`p-6 rounded-sm border mb-8 ${status === 'verified' ? 'border-green-500/30 bg-green-500/5' : 'border-border bg-card'}`}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${statusConfig[status]?.color}`}>
                <StatusIcon className="w-6 h-6" />
              </div>
              <div>
                <Badge className={statusConfig[status]?.color}>
                  {statusConfig[status]?.label}
                </Badge>
                <p className="text-sm text-muted-foreground mt-1">
                  {statusConfig[status]?.description}
                </p>
                {verificationStatus?.verification_notes && status === 'rejected' && (
                  <p className="text-sm text-red-500 mt-2">
                    <strong>Reason:</strong> {verificationStatus.verification_notes}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Verification Form - Show only if pending or rejected */}
          {(status === 'pending' || status === 'rejected') && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* ABN */}
              <div className="bg-card border border-border rounded-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                    BUSINESS DETAILS
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="abn">Australian Business Number (ABN) *</Label>
                    <Input
                      id="abn"
                      placeholder="XX XXX XXX XXX"
                      value={formData.abn}
                      onChange={(e) => setFormData({ ...formData, abn: formatABN(e.target.value) })}
                      maxLength={14}
                      data-testid="abn-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Your 11-digit ABN for business verification
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Business/Company Name (Optional)</Label>
                    <Input
                      id="company_name"
                      placeholder="Your Business Name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      data-testid="company-name-input"
                    />
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="bg-card border border-border rounded-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                    REQUIRED DOCUMENTS
                  </h3>
                </div>
                
                <div className="space-y-4">
                  {/* Photo ID */}
                  <div className="space-y-2">
                    <Label>Photo ID (Driver's License or Passport) *</Label>
                    <div className="border-2 border-dashed border-border rounded-sm p-4 text-center hover:border-primary/50 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange('photo_id', e.target.files[0])}
                        className="hidden"
                        id="photo_id"
                        data-testid="photo-id-input"
                      />
                      <label htmlFor="photo_id" className="cursor-pointer">
                        {files.photo_id ? (
                          <div className="flex items-center justify-center gap-2 text-green-500">
                            <CheckCircle className="w-5 h-5" />
                            <span>{files.photo_id.name}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <Upload className="w-8 h-8" />
                            <span>Click to upload Photo ID</span>
                            <span className="text-xs">PDF, JPG, PNG (Max 5MB)</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Insurance */}
                  <div className="space-y-2">
                    <Label>Insurance Certificate *</Label>
                    <div className="border-2 border-dashed border-border rounded-sm p-4 text-center hover:border-primary/50 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange('insurance', e.target.files[0])}
                        className="hidden"
                        id="insurance"
                        data-testid="insurance-input"
                      />
                      <label htmlFor="insurance" className="cursor-pointer">
                        {files.insurance ? (
                          <div className="flex items-center justify-center gap-2 text-green-500">
                            <CheckCircle className="w-5 h-5" />
                            <span>{files.insurance.name}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <Upload className="w-8 h-8" />
                            <span>Click to upload Insurance Certificate</span>
                            <span className="text-xs">PDF, JPG, PNG (Max 5MB)</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Business Registration */}
                  <div className="space-y-2">
                    <Label>Business Registration (Optional)</Label>
                    <div className="border-2 border-dashed border-border rounded-sm p-4 text-center hover:border-primary/50 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange('business_registration', e.target.files[0])}
                        className="hidden"
                        id="business_registration"
                        data-testid="business-reg-input"
                      />
                      <label htmlFor="business_registration" className="cursor-pointer">
                        {files.business_registration ? (
                          <div className="flex items-center justify-center gap-2 text-green-500">
                            <CheckCircle className="w-5 h-5" />
                            <span>{files.business_registration.name}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <Upload className="w-8 h-8" />
                            <span>Click to upload Business Registration</span>
                            <span className="text-xs">PDF, JPG, PNG (Max 5MB)</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notice */}
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-sm">
                <AlertCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Your documents are securely stored and only reviewed by our verification team. 
                  Verification helps protect customers and builds trust in the marketplace.
                </p>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full btn-active"
                disabled={submitting}
                data-testid="submit-verification-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Submit for Verification
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Under Review Message */}
          {status === 'under_review' && (
            <div className="text-center p-8 bg-card border border-border rounded-sm">
              <Clock className="w-12 h-12 text-blue-500 mx-auto mb-4" />
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                DOCUMENTS UNDER REVIEW
              </h3>
              <p className="text-muted-foreground">
                Our team is reviewing your documents. You'll receive a notification once the review is complete.
                This typically takes 24-48 hours.
              </p>
            </div>
          )}

          {/* Verified Message */}
          {status === 'verified' && (
            <div className="text-center p-8 bg-card border border-green-500/30 rounded-sm">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                ACCOUNT VERIFIED
              </h3>
              <p className="text-muted-foreground mb-4">
                Your account is fully verified. Customers can see your verified badge.
              </p>
              <Link to="/provider">
                <Button className="btn-active">Go to Dashboard</Button>
              </Link>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
